function normalizeBase(base?: string) {
  if (!base) return '/api';
  try {
    const u = new URL(base);
    if ((u.hostname === 'localhost' || u.hostname === '127.0.0.1') && !u.pathname.includes('/api')) {
      return base.replace(/\/?$/, '') + '/api';
    }
  } catch {
    // base is likely a relative path like '/api'
  }
  return base;
}

const API_BASE = normalizeBase(import.meta.env.VITE_API_BASE_URL);

export interface ApiClientConfig {
  token?: string;
}

export class ApiClient {
  private baseUrl: string;
  private token?: string;

  constructor(config?: ApiClientConfig) {
    this.baseUrl = API_BASE;
    this.token = config?.token;
  }

  setToken(token: string | null) {
    this.token = token || undefined;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      if (response.status === 404) {
        throw new Error('Authentication service is currently unavailable. Please try again later.');
      }
      throw new Error(error.error || `Request failed with status ${response.status}`);
    }

    return response.json();
  }

  async requestText(endpoint: string, options: RequestInit = {}): Promise<string> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      if (response.status === 404) {
        throw new Error('Authentication service is currently unavailable. Please try again later.');
      }
      throw new Error(error.error || `Request failed with status ${response.status}`);
    }

    return response.text();
  }

  // Auth endpoints
  async login(username: string, password: string) {
    return this.request<{jwt: string}>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async getMe() {
    return this.request<any>('/auth/me');
  }

  async register(userData: any) {
    return this.request<any>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Generic methods for other endpoints
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint: string): Promise<string> {
    return this.requestText(endpoint, { method: 'DELETE' });
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
